import { authedFetch } from './api.ts';

/**
 * Fetching profile pictures so they can actually be displayed.
 *
 * The obvious implementation — `<img src="/api/users/:id/avatar">` — cannot
 * work here and quietly fails in the worst way. The access token lives in
 * memory and travels as an Authorization header, which an `<img>` tag has no
 * way to send, and the refresh cookie is scoped to `/api/auth` so it is not
 * sent either. The request arrives unauthenticated, comes back 401, and the
 * picture silently never appears.
 *
 * So the bytes are fetched like any other authenticated request and turned
 * into an object URL.
 *
 * Cached by person and version, because the alternative is one request per
 * row: the audit log renders an avatar beside every entry, and fifty rows by
 * six people should be six requests, not fifty. The cache holds the promise
 * rather than the result, so simultaneous rows asking for the same picture
 * share one request instead of racing.
 */

/** Object URLs are cheap but not free. Keep the most recent few. */
const MAX_ENTRIES = 40;

const cache = new Map<string, Promise<string | null>>();

function keyFor(userId: string, version: string | undefined): string {
  return `${userId}@${version ?? ''}`;
}

export function avatarSource(userId: string, version: string | undefined): Promise<string | null> {
  const key = keyFor(userId, version);

  const existing = cache.get(key);
  if (existing) return existing;

  const pending = (async (): Promise<string | null> => {
    try {
      const response = await authedFetch(`/api/users/${userId}/avatar`, {
        credentials: 'include',
      });
      // 404 is ordinary — it means no picture, which is not an error worth
      // reporting anywhere. Anything else is treated the same way: fall back
      // to initials rather than show a broken image.
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    } catch {
      return null;
    }
  })();

  cache.set(key, pending);

  // Evict in insertion order once the cache grows past its cap. Revoking
  // matters: an object URL pins the whole image in memory until it is.
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next();
    if (!oldest.done) {
      const stale = cache.get(oldest.value);
      cache.delete(oldest.value);
      void stale?.then((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    }
  }

  return pending;
}

/**
 * Forget a person's cached picture.
 *
 * Called after uploading or removing your own, so the next render fetches
 * again rather than showing what was there before. The version stamp in the
 * key usually handles this on its own — but only once the new profile has
 * been read back, and the upload finishes before that.
 */
export function forgetAvatar(userId: string): void {
  for (const key of [...cache.keys()]) {
    if (!key.startsWith(`${userId}@`)) continue;
    const stale = cache.get(key);
    cache.delete(key);
    void stale?.then((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }
}
